import { bindOrderNotesNotifyConfirm } from '../share/order-notes-notify-confirm';

/**
 * 購入者共有・工事画像は x-file-manager の `file_status` を利用する。
 * ワイヤ値の定義は `resources/js/enums/file-manager-file-status.js`（PHP: `App\Enums\FileManagerFileStatus`）と揃える。
 */

/**
 * 帯色用: [hidden] 配下の行は DOM に残るが表示上は出ないためストライプ対象外（トグル後に再計算する）。
 *
 * @param {Element} row
 * @returns {boolean}
 */
function ordersEditStripeRowIsHidden(row) {
    return row.closest('[hidden]') !== null;
}

/**
 * 受注編集: 各 form-table-block__body 内で、先頭行をグレーとし可視行のみ交互の帯にする。
 * 区切り行は「次の帯がグレーになる位置」では行を出さず直前の白行の下辺に線を付ける。
 *
 * @param {HTMLElement} body
 * @returns {void}
 */
function ordersEditApplyStripingToBlockBody(body) {
    body.querySelectorAll('.form-table-row').forEach((row) => {
        row.classList.remove(
            'orders-edit-stripe--grey',
            'orders-edit-stripe--white',
            'orders-edit-stripe-divider-below',
        );
        if (row.classList.contains('orders-edit-form-divider-row')) {
            row.classList.remove('orders-edit-form-divider-row--merged-slot');
        }
    });

    let stripeRowCount = 0;
    /** @type {HTMLElement|null} */
    let lastStripeRow = null;

    /**
     * @param {HTMLElement} container
     * @returns {void}
     */
    const walk = (container) => {
        const children = Array.from(container.children);
        for (const child of children) {
            if (!(child instanceof HTMLElement)) {
                continue;
            }
            if (child.classList.contains('form-table-row')) {
                if (child.classList.contains('orders-edit-form-divider-row')) {
                    const nextStripeIsGrey = stripeRowCount % 2 === 0;
                    if (nextStripeIsGrey) {
                        child.classList.add('orders-edit-form-divider-row--merged-slot');
                        if (lastStripeRow) {
                            lastStripeRow.classList.add('orders-edit-stripe-divider-below');
                        }
                    }
                    continue;
                }
                if (ordersEditStripeRowIsHidden(child)) {
                    continue;
                }
                const isGrey = stripeRowCount % 2 === 0;
                child.classList.add(isGrey ? 'orders-edit-stripe--grey' : 'orders-edit-stripe--white');
                stripeRowCount += 1;
                lastStripeRow = child;
                continue;
            }
            walk(child);
        }
    };

    walk(body);
}

/**
 * @returns {void}
 */
function ordersEditApplyFormTableStriping() {
    const root = document.getElementById('admin-orders-edit-root');
    if (!root) {
        return;
    }
    root.querySelectorAll('.form-table-block__body').forEach((body) => {
        if (body instanceof HTMLElement) {
            ordersEditApplyStripingToBlockBody(body);
        }
    });
}

/**
 * 受注履歴: 番号を選び「参照」で order_history_id 付き GET へ遷移する。
 * 現在受注表示中は hidden の空 option が selected のため value が空のときは何もしない。
 */
function ordersEditBindHistoryReferenceButton() {
    const root = document.getElementById('admin-orders-edit-root');
    const historySelect = document.getElementById('orders_edit_history_select');
    const referenceButton = document.querySelector('.orders-edit-history-reference-btn');
    if (!root || !historySelect || !referenceButton) {
        return;
    }
    const baseUrl = root.getAttribute('data-order-edit-base-url') ?? '';
    if (baseUrl === '') {
        return;
    }
    referenceButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (historySelect.disabled || referenceButton.hasAttribute('disabled')) {
            return;
        }
        const selectedHistoryId = String(historySelect.value).trim();
        if (selectedHistoryId === '') {
            return;
        }
        const historySep = baseUrl.includes('?') ? '&' : '?';
        const nextUrl = `${baseUrl}${historySep}order_history_id=${encodeURIComponent(selectedHistoryId)}`;
        window.location.assign(nextUrl);
    });
}

/**
 * 受注履歴参照モード: 「最新表示」で order_history_id なしの編集画面（現在の受注・明細）へ遷移する。
 */
function ordersEditBindHistoryShowCurrentButton() {
    const root = document.getElementById('admin-orders-edit-root');
    const historySelect = document.getElementById('orders_edit_history_select');
    const showCurrentButton = document.querySelector('.orders-edit-history-show-current-btn');
    if (!root || !historySelect || !showCurrentButton) {
        return;
    }
    const baseUrl = root.getAttribute('data-order-edit-base-url') ?? '';
    if (baseUrl === '') {
        return;
    }
    showCurrentButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (historySelect.disabled || showCurrentButton.hasAttribute('disabled')) {
            return;
        }
        window.location.assign(baseUrl);
    });
}

/**
 * 受注履歴参照モード: 「復元する」で確認モーダル表示後、POST で復元する（platform admin のみ）。
 */
function ordersEditBindHistoryRestoreButton() {
    const root = document.getElementById('admin-orders-edit-root');
    const restoreButton = document.querySelector('.orders-edit-history-restore-btn');
    const restoreForm = document.getElementById('orders_edit_history_restore_form');
    const modalElement = document.getElementById('adminOrdersHistoryRestoreModal');
    const yesButton = document.getElementById('adminOrdersHistoryRestoreYes');
    if (
        root === null ||
        !(restoreButton instanceof HTMLButtonElement) ||
        !(restoreForm instanceof HTMLFormElement) ||
        modalElement === null ||
        root.dataset.orderEditReadOnly !== '1'
    ) {
        return;
    }

    restoreButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (restoreButton.hasAttribute('disabled')) {
            return;
        }

        const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
        modal.show();
    });

    if (!(yesButton instanceof HTMLButtonElement)) {
        return;
    }

    yesButton.addEventListener('click', () => {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal !== null) {
            modal.hide();
        }
        restoreForm.submit();
    });
}

/**
 * 管理画面・受注情報編集: 注文ステータス select とステッパー表示を同期
 */
function ordersEditBindStatusStepper() {
    const root = document.querySelector('[data-orders-status-stepper]');
    const select = document.getElementById('order_status');
    const messageEl = document.getElementById('orders-edit-status-stepper-message');
    const captionsEl = document.getElementById('orders-edit-stepper-captions');
    if (!root) {
        return;
    }
    if (!select) {
        return;
    }

    let captions = {};
    if (captionsEl && captionsEl.textContent.trim() !== '') {
        try {
            captions = JSON.parse(captionsEl.textContent);
        } catch {
            captions = {};
        }
    }

    const apply = () => {
        const value = String(select.value);
        root.querySelectorAll('[data-step-value]').forEach((segment) => {
            const matches = String(segment.getAttribute('data-step-value')) === value;
            segment.classList.toggle('is-active', matches);
            if (matches) {
                segment.setAttribute('aria-current', 'step');
            } else {
                segment.removeAttribute('aria-current');
            }
        });
        if (messageEl) {
            const text = captions[value];
            messageEl.textContent = typeof text === 'string' ? text : '';
        }
    };

    select.addEventListener('change', apply);
    apply();
}

/**
 * 管理画面・受注情報編集: 商品選択モーダル（【AC0014】）からフォームへ反映
 */
function ordersEditBindProductModal() {
    const root = document.getElementById('admin-orders-edit-root');
    if (!root) {
        return;
    }
    if (root.dataset.orderEditReadOnly === '1') {
        ordersEditApplyFormTableStriping();

        return;
    }

    const yenSuffix = root.dataset.yenSuffix ?? '';
    const noImageUrl = root.dataset.noImageUrl ?? '';
    const pinLineCatalogProductId = String(root.dataset.ordersEditPinLineProductId ?? '') === '1';
    const lineCatalogProductIdFromPage = String(root.dataset.ordersEditLineCatalogProductId ?? '').trim();

    const openButton = document.getElementById('btn-orders-edit-open-product-modal');
    const clearButton = document.getElementById('btn-orders-edit-clear-product-modal');

    const setProductModalClearVisible = (isVisible) => {
        if (!clearButton) {
            return;
        }
        if (isVisible) {
            clearButton.removeAttribute('hidden');
        } else {
            clearButton.setAttribute('hidden', '');
        }
    };

    const syncClearButtonFromProductIdField = () => {
        const productIdInput = document.getElementById('orders_edit_product_id');
        const estimateProductIdInput = document.getElementById('orders_edit_estimate_product_id');
        const productIdRaw =
            productIdInput && 'value' in productIdInput ? String(productIdInput.value ?? '').trim() : '';
        const estimateProductIdRaw =
            estimateProductIdInput && 'value' in estimateProductIdInput
                ? String(estimateProductIdInput.value ?? '').trim()
                : '';
        const hasProduct = pinLineCatalogProductId
            ? estimateProductIdRaw !== '' && estimateProductIdRaw !== '0'
            : productIdRaw !== '' && productIdRaw !== '0';
        setProductModalClearVisible(hasProduct);
    };

    const formatYen = (raw) => {
        if (raw === undefined || raw === null || raw === '') {
            return '';
        }
        const n = Number(raw);
        if (Number.isNaN(n)) {
            return '';
        }
        return new Intl.NumberFormat('ja-JP').format(n) + yenSuffix;
    };

    const setVal = (id, value) => {
        const str = value === undefined || value === null ? '' : String(value);
        root.querySelectorAll(`[id="${id}"]`).forEach((el) => {
            if ('value' in el) {
                el.value = str;
            }
        });
    };

    /**
     * id 重複や DOM 構造の差で setVal が当たらない場合でも、POST に乗る name= の hidden を確実に更新する。
     *
     * @param {string} fieldName
     * @param {unknown} raw
     * @returns {void}
     */
    const setOrderFormHiddenFieldValueByName = (fieldName, raw) => {
        const form = document.getElementById('order_edit_form');
        if (!(form instanceof HTMLFormElement)) {
            return;
        }
        const input = form.querySelector(`input[name="${CSS.escape(fieldName)}"]`);
        if (input && 'value' in input) {
            const str = raw === undefined || raw === null ? '' : String(raw);
            /** @type {HTMLInputElement} */ (input).value = str;
        }
    };

    const setReadonlyLabel = (forKey, value) => {
        const t = value === undefined || value === null || value === '' ? '' : String(value);
        root.querySelectorAll(`[data-orders-edit-label-for="${forKey}"]`).forEach((labelEl) => {
            labelEl.textContent = t;
        });
    };

    /** 購入商品情報・見積ブロックの「カテゴリ」表示は sub_category_name のみ（PHP 初期表示と揃える）。 */
    const buildCategoryDisplay = (_categoryName, subCategoryName) => {
        if (subCategoryName === undefined || subCategoryName === null) {
            return '';
        }

        return String(subCategoryName).trim();
    };

    const estimateProductBlock = root.querySelector('.orders-edit-product-estimate-block');

    /**
     * 見積ブロック内の input を優先（hidden 用と重複 id があるとき getElementById だと古い hidden だけが返るため）。
     *
     * @param {string} elementId
     * @returns {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null}
     */
    const getOrderEditProductFieldInput = (elementId) => {
        if (estimateProductBlock) {
            const inBlock = estimateProductBlock.querySelector(`#${CSS.escape(elementId)}`);
            if (inBlock && 'value' in inBlock) {
                return /** @type {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} */ (inBlock);
            }
        }
        const el = document.getElementById(elementId);
        return el && 'value' in el ? /** @type {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} */ (el) : null;
    };

    const setEstimateBlockReadonlyLabel = (forKey, value) => {
        if (!estimateProductBlock) {
            return;
        }
        const labelEl = estimateProductBlock.querySelector(`[data-orders-edit-estimate-label-for="${forKey}"]`);
        if (labelEl) {
            const t = value === undefined || value === null || value === '' ? '' : String(value);
            labelEl.textContent = t;
        }
    };

    /**
     * @param {Record<string, unknown>|null|undefined} data When set, values come from the product API payload; otherwise from form inputs.
     */
    const syncEstimateBlockReadonlyLabels = (data) => {
        if (!estimateProductBlock) {
            return;
        }
        let sellRaw;
        let costRaw;
        let categoryName;
        let subCategoryName;
        let manufacturer;
        let modelNumber;
        let productName;
        let item;

        if (data && typeof data === 'object') {
            sellRaw = data.sell_price;
            costRaw = data.cost_price;
            categoryName = data.category_name;
            subCategoryName = data.sub_category ?? data.sub_category_name;
            manufacturer = data.manufacturer;
            modelNumber = data.model_number;
            productName = data.product_name;
            item = data.item;
        } else {
            const sellInput = getOrderEditProductFieldInput('orders_edit_sell_price');
            const costInput = getOrderEditProductFieldInput('orders_edit_cost');
            const manufacturerInput = getOrderEditProductFieldInput('orders_edit_manufacturer_name');
            const modelInput = getOrderEditProductFieldInput('orders_edit_model_number');
            const productNameInput = getOrderEditProductFieldInput('orders_edit_product_name');
            const itemInput = getOrderEditProductFieldInput('orders_edit_item');
            sellRaw = sellInput ? sellInput.value : '';
            costRaw = costInput ? costInput.value : '';
            manufacturer = manufacturerInput ? manufacturerInput.value : '';
            modelNumber = modelInput ? modelInput.value : '';
            productName = productNameInput ? productNameInput.value : '';
            item = itemInput ? itemInput.value : '';

            const categorySubSelect = document.getElementById('orders_edit_category_sub_select');
            const opt =
                categorySubSelect && categorySubSelect.selectedOptions.length > 0
                    ? categorySubSelect.selectedOptions[0]
                    : null;
            if (opt && categorySubSelect && categorySubSelect.value !== '') {
                categoryName = opt.getAttribute('data-category-name') ?? '';
                subCategoryName = opt.getAttribute('data-sub-category-name') ?? '';
            } else {
                const categoryNameHidden = document.getElementById('orders_edit_category_name');
                const subCategoryNameHidden = document.getElementById('orders_edit_sub_category_name');
                categoryName =
                    categoryNameHidden && 'value' in categoryNameHidden
                        ? categoryNameHidden.value
                        : '';
                subCategoryName =
                    subCategoryNameHidden && 'value' in subCategoryNameHidden
                        ? subCategoryNameHidden.value
                        : '';
            }
        }

        setEstimateBlockReadonlyLabel('sell_price', formatYen(sellRaw));
        setEstimateBlockReadonlyLabel('cost', formatYen(costRaw));
        setEstimateBlockReadonlyLabel('category_combined', buildCategoryDisplay(categoryName, subCategoryName));
        setEstimateBlockReadonlyLabel('manufacturer_name', manufacturer);
        setEstimateBlockReadonlyLabel('model_number', modelNumber);
        setEstimateBlockReadonlyLabel('product_name', productName);
        setEstimateBlockReadonlyLabel('item', item);
    };

    const clearOrderEditCategoryHiddens = () => {
        setVal('orders_edit_sub_category_id', '');
        setVal('orders_edit_category_id', '');
        setVal('orders_edit_category_name', '');
        setVal('orders_edit_sub_category_name', '');
    };

    const applyOrderEditCategoryOptionToHiddens = (selectEl) => {
        if (!selectEl) {
            return;
        }
        const opt = selectEl.selectedOptions[0];
        if (!opt || opt.value === '') {
            return;
        }
        setVal('orders_edit_sub_category_id', opt.value);
        setVal('orders_edit_category_id', opt.getAttribute('data-category-id') ?? '');
        setVal('orders_edit_category_name', opt.getAttribute('data-category-name') ?? '');
        setVal('orders_edit_sub_category_name', opt.getAttribute('data-sub-category-name') ?? '');
    };

    const productGallerySlotCount = 3;

    const setProductGallery = (urls) => {
        const wrap =
            root.querySelector('[data-orders-edit-product-gallery-selected]') ??
            root.querySelector('[data-orders-edit-product-gallery]');
        if (!wrap) {
            return;
        }
        const list = Array.isArray(urls) ? urls.filter((u) => typeof u === 'string' && u !== '') : [];
        const capped = list.slice(0, productGallerySlotCount);
        wrap.innerHTML = '';
        for (let i = 0; i < productGallerySlotCount; i += 1) {
            const slot = document.createElement('div');
            slot.className =
                'orders-edit-product-images__slot orders-edit-product-images__slot--photo';
            const img = document.createElement('img');
            const url = capped[i];
            if (url) {
                img.src = url;
            } else {
                img.src = noImageUrl || '';
                img.setAttribute('data-orders-edit-no-image', '1');
            }
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            slot.appendChild(img);
            wrap.appendChild(slot);
        }
    };

    const applyProductPayload = (data) => {
        const categoryName = data.category_name;
        const subCategoryName = data.sub_category ?? data.sub_category_name;
        const selectedCatalogProductId = data.product_id;
        let estimateHiddenForFormPost = '';
        if (pinLineCatalogProductId && lineCatalogProductIdFromPage !== '') {
            setVal('orders_edit_product_id', lineCatalogProductIdFromPage);
            setVal('orders_edit_estimate_product_id', selectedCatalogProductId);
            estimateHiddenForFormPost =
                selectedCatalogProductId === undefined || selectedCatalogProductId === null
                    ? ''
                    : String(selectedCatalogProductId);
        } else {
            setVal('orders_edit_product_id', selectedCatalogProductId);
            setVal('orders_edit_estimate_product_id', '');
            estimateHiddenForFormPost = '';
        }
        setOrderFormHiddenFieldValueByName('estimate_product_id', estimateHiddenForFormPost);
        setVal('orders_edit_category_id', data.category_id);
        setVal('orders_edit_sub_category_id', data.sub_category_id);
        setVal('orders_edit_category_name', categoryName);
        setVal('orders_edit_sub_category_name', subCategoryName);
        setVal('orders_edit_product_name', data.product_name);
        setVal('orders_edit_manufacturer_name', data.manufacturer);
        setVal('orders_edit_model_number', data.model_number);
        setVal('orders_edit_item', data.item);
        setVal('orders_edit_cost', data.cost_price);
        setVal('orders_edit_sell_price', data.sell_price);

        const pidForDisplay =
            pinLineCatalogProductId && selectedCatalogProductId !== undefined && selectedCatalogProductId !== null
                ? selectedCatalogProductId
                : data.product_id;
        setReadonlyLabel(
            'orders_edit_product_id_display',
            pidForDisplay === undefined || pidForDisplay === null || pidForDisplay === '' ? '' : String(pidForDisplay),
        );
        setReadonlyLabel('orders_edit_category_combined', buildCategoryDisplay(categoryName, subCategoryName));
        setReadonlyLabel('orders_edit_manufacturer_name', data.manufacturer);
        setReadonlyLabel('orders_edit_product_name', data.product_name);
        setReadonlyLabel('orders_edit_model_number', data.model_number);
        setReadonlyLabel('orders_edit_item', data.item);
        setReadonlyLabel('orders_edit_sell_price_display', formatYen(data.sell_price));

        const galleryUrls = data.product_gallery_urls;
        setProductGallery(Array.isArray(galleryUrls) ? galleryUrls : []);

        const categorySubSelect = document.getElementById('orders_edit_category_sub_select');
        if (categorySubSelect) {
            const subId = data.sub_category_id;
            if (subId !== undefined && subId !== null && subId !== '') {
                categorySubSelect.value = String(subId);
            } else {
                categorySubSelect.value = '';
            }
        }

        syncEstimateBlockReadonlyLabels(data);
        syncClearButtonFromProductIdField();
    };

    /**
     * 商品選択を空にする（ページ初期表示時点のスナップショットへ戻すのではなく、見積ブロックの選択をクリアする）。
     */
    const clearOrderEditProductEstimateSelection = () => {
        const idsToClear = [
            'orders_edit_category_id',
            'orders_edit_sub_category_id',
            'orders_edit_category_name',
            'orders_edit_sub_category_name',
            'orders_edit_item',
            'orders_edit_cost',
            'orders_edit_sell_price',
            'orders_edit_manufacturer_name',
            'orders_edit_product_name',
            'orders_edit_model_number',
        ];
        idsToClear.forEach((inputId) => setVal(inputId, ''));
        if (pinLineCatalogProductId) {
            setVal('orders_edit_estimate_product_id', '');
            if (lineCatalogProductIdFromPage !== '') {
                setVal('orders_edit_product_id', lineCatalogProductIdFromPage);
            } else {
                setVal('orders_edit_product_id', '');
            }
        } else {
            setVal('orders_edit_product_id', '');
            setVal('orders_edit_estimate_product_id', '');
        }

        const categorySelect = document.getElementById('orders_edit_category_sub_select');
        if (categorySelect) {
            categorySelect.value = '';
        }
        clearOrderEditCategoryHiddens();

        [
            'orders_edit_product_id_display',
            'orders_edit_category_combined',
            'orders_edit_manufacturer_name',
            'orders_edit_product_name',
            'orders_edit_model_number',
            'orders_edit_item',
            'orders_edit_sell_price_display',
        ].forEach((labelKey) => setReadonlyLabel(labelKey, ''));

        setProductGallery([]);

        const regCheckbox = document.getElementById('orders_edit_register_as_new_product');
        const editWrap = root.querySelector('[data-orders-edit-product-estimate-editable]');
        const readWrap = root.querySelector('[data-orders-edit-product-estimate-readonly]');
        if (regCheckbox) {
            regCheckbox.checked = false;
        }
        if (editWrap && readWrap) {
            editWrap.setAttribute('hidden', '');
            readWrap.removeAttribute('hidden');
        }

        syncEstimateBlockReadonlyLabels(null);
        syncClearButtonFromProductIdField();
        setOrderFormHiddenFieldValueByName('estimate_product_id', '');
        ordersEditApplyFormTableStriping();
    };

    if (openButton) {
        openButton.addEventListener('click', () => {
            window.dispatchEvent(
                new CustomEvent('item-selector-modal-request-open', {
                    bubbles: true,
                    detail: {
                        modalInstanceId: 'orders-edit',
                        listType: 'product',
                        browserEventName: 'orders-edit-product-select',
                    },
                }),
            );
        });
    }

    if (clearButton) {
        clearButton.addEventListener('click', () => {
            clearOrderEditProductEstimateSelection();
        });
    }

    const categorySubSelect = document.getElementById('orders_edit_category_sub_select');
    if (categorySubSelect) {
        categorySubSelect.addEventListener('change', () => {
            if (categorySubSelect.value === '') {
                clearOrderEditCategoryHiddens();
            } else {
                applyOrderEditCategoryOptionToHiddens(categorySubSelect);
            }
            syncEstimateBlockReadonlyLabels(null);
        });
        if (categorySubSelect.value !== '') {
            applyOrderEditCategoryOptionToHiddens(categorySubSelect);
        }
    }

    const registerAsNewProductCheckbox = document.getElementById('orders_edit_register_as_new_product');
    const estimateEditableWrap = root.querySelector('[data-orders-edit-product-estimate-editable]');
    const estimateReadonlyWrap = root.querySelector('[data-orders-edit-product-estimate-readonly]');
    if (registerAsNewProductCheckbox && estimateEditableWrap && estimateReadonlyWrap) {
        const applyRegisterAsNewProductMode = () => {
            const checked = registerAsNewProductCheckbox.checked;
            if (!checked) {
                syncEstimateBlockReadonlyLabels(null);
            }
            if (checked) {
                estimateEditableWrap.removeAttribute('hidden');
                estimateReadonlyWrap.setAttribute('hidden', '');
            } else {
                estimateEditableWrap.setAttribute('hidden', '');
                estimateReadonlyWrap.removeAttribute('hidden');
            }
            ordersEditApplyFormTableStriping();
        };
        registerAsNewProductCheckbox.addEventListener('change', applyRegisterAsNewProductMode);
    }

    if (estimateProductBlock) {
        const sellPriceInput = document.getElementById('orders_edit_sell_price');
        const costInput = document.getElementById('orders_edit_cost');
        ['input', 'change'].forEach((eventName) => {
            if (sellPriceInput) {
                sellPriceInput.addEventListener(eventName, () => syncEstimateBlockReadonlyLabels(null));
            }
            if (costInput) {
                costInput.addEventListener(eventName, () => syncEstimateBlockReadonlyLabels(null));
            }
        });
        const textSyncIds = [
            'orders_edit_manufacturer_name',
            'orders_edit_model_number',
            'orders_edit_product_name',
            'orders_edit_item',
        ];
        textSyncIds.forEach((elementId) => {
            const textInput = document.getElementById(elementId);
            if (textInput) {
                textInput.addEventListener('input', () => syncEstimateBlockReadonlyLabels(null));
            }
        });
    }

    document.addEventListener('livewire:init', () => {
        if (window.__jpenergyOrdersEditProductListenerRegistered) {
            return;
        }
        window.__jpenergyOrdersEditProductListenerRegistered = true;

        Livewire.on('orders-edit-product-select', async (payload) => {
            const productId = payload?.id ?? payload?.detail?.id;
            if (productId === undefined || productId === null || productId === '') {
                return;
            }
            try {
                const data = await window.jpenergyFetchAdminProductById(productId);
                applyProductPayload(data);
            } catch (error) {
                const message =
                    error && typeof error.message === 'string' ? error.message : String(error);
                if (typeof window.showAlert === 'function') {
                    window.showAlert(message);
                }
            }
        });

        syncClearButtonFromProductIdField();
    });

    syncClearButtonFromProductIdField();
    ordersEditApplyFormTableStriping();
}

/**
 * 見積書ダウンロード: フォーム内の GET ダウンロード URL は target=_blank だけでは新規タブにならない環境がある。
 * 通常クリックでは preventDefault 後に window.open で新規タブを開く（修飾キー・中ボタンはブラウザ既定のまま）。
 *
 * @returns {void}
 */
function ordersEditBindEstimateDownloadOpenInNewTab() {
    const root = document.getElementById('admin-orders-edit-root');
    if (!root) {
        return;
    }
    root.querySelectorAll('a.orders-edit-order-info-footer__btn-download').forEach((node) => {
        if (!(node instanceof HTMLAnchorElement)) {
            return;
        }
        const hrefAttribute = node.getAttribute('href');
        if (hrefAttribute === null || hrefAttribute === '' || hrefAttribute === '#') {
            return;
        }
        node.addEventListener('click', (event) => {
            if (event.defaultPrevented) {
                return;
            }
            if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                event.button !== 0
            ) {
                return;
            }
            event.preventDefault();
            window.open(node.href, '_blank', 'noopener,noreferrer');
        });
    });
}

ordersEditBindHistoryReferenceButton();
ordersEditBindHistoryShowCurrentButton();
ordersEditBindHistoryRestoreButton();
ordersEditBindStatusStepper();
ordersEditBindProductModal();
ordersEditBindEstimateDownloadOpenInNewTab();

/**
 * 連絡事項変更時: 登録前に購入者通知の確認モーダルを表示する（はい/いいえ/X）。
 *
 * @returns {void}
 */
function ordersEditBindNotesChangeNotifyConfirm() {
    bindOrderNotesNotifyConfirm({
        formId: 'order_edit_form',
        notesFieldId: 'order_notes',
        notifyFieldId: 'notify_buyer_on_notes_change',
        modalId: 'adminOrdersNotesNotifyModal',
        yesButtonId: 'adminOrdersNotesNotifyYes',
        noButtonId: 'adminOrdersNotesNotifyNo',
        skipWhen: {
            rootId: 'admin-orders-edit-root',
            dataAttribute: 'orderEditReadOnly',
            skipValue: '1',
        },
    });
}

ordersEditBindNotesChangeNotifyConfirm();
